import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationModal from "../../../Shared/confirmationModal";
import ContentSectionPanel from "../../content/ContentSectionPanel";
import {
  cancelScheduledNewsletterHistory,
  getNewsletterHistoryDetail,
  searchNewsletterHistory,
} from "../../../../services/newsletterServices";
import NewsletterHistoryListPanel from "./NewsletterHistoryListPanel";
import NewsletterHistoryDetailPanel from "./NewsletterHistoryDetailPanel";
import { NEWSLETTER_HISTORY_STATUS_FILTER_OPTIONS } from "./newsletterHistoryStatusUtils";
import { contentFieldSx } from "../../content/contentEditorStyles";
import { useNewsletterPublishFeedback } from "./useNewsletterPublishFeedback";

const PAGE_SIZE = 20;

const NewsletterHistoryView = ({ onNavigatePublish }) => {
  const notify = useNewsletterPublishFeedback();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchFieldValue, setSearchFieldValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchFieldValue), 300);
    return () => clearTimeout(timer);
  }, [searchFieldValue]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await searchNewsletterHistory({
        searchFieldValue: debouncedSearch,
        status: statusFilter.length ? statusFilter : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(data.items);
      setCount(data.count);
    } catch (err) {
      notify({ title: "Errore storico", message: err.message });
      setItems([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, dateFrom, dateTo, page, notify]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    getNewsletterHistoryDetail(selectedId)
      .then(setDetail)
      .catch((err) => {
        notify({ title: "Errore dettaglio", message: err.message });
        setDetail(null);
        setSelectedId(null);
      })
      .finally(() => setDetailLoading(false));
  }, [selectedId, notify]);

  const resetFilters = () => {
    setSearchFieldValue("");
    setStatusFilter([]);
    setDateFrom("");
    setDateTo("");
    setPage(0);
  };

  const hasFilters = debouncedSearch || statusFilter.length || dateFrom || dateTo;
  const showEmptyState = !loading && count === 0 && !hasFilters && !selectedId;

  const handleCancelScheduled = (item) => {
    setCancelTarget(item);
  };

  const confirmCancelScheduled = async () => {
    if (!cancelTarget) {
      return;
    }
    const item = cancelTarget;
    setCancelTarget(null);
    setCancellingId(item.id);
    try {
      await cancelScheduledNewsletterHistory(item.id);
      notify({
        title: "Invio annullato",
        message: "La newsletter programmata è stata annullata.",
      });
      if (selectedId === item.id) {
        setSelectedId(null);
      }
      await fetchList();
    } catch (err) {
      notify({ title: "Annullamento non riuscito", message: err.message });
    } finally {
      setCancellingId(null);
    }
  };

  const renderFiltersToolbar = () => (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ flex: 1, justifyContent: "flex-end" }}
    >
      <TextField
        size="small"
        placeholder="Cerca oggetto..."
        value={searchFieldValue}
        onChange={(event) => {
          setSearchFieldValue(event.target.value);
          setPage(0);
        }}
        sx={{ minWidth: 140, maxWidth: 220, ...contentFieldSx }}
      />
      <TextField
        size="small"
        type="date"
        label="Da"
        value={dateFrom}
        onChange={(event) => {
          setDateFrom(event.target.value);
          setPage(0);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ width: 130, ...contentFieldSx }}
      />
      <TextField
        size="small"
        type="date"
        label="A"
        value={dateTo}
        onChange={(event) => {
          setDateTo(event.target.value);
          setPage(0);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ width: 130, ...contentFieldSx }}
      />
      <FormControl size="small" sx={{ minWidth: 130, ...contentFieldSx }}>
        <InputLabel shrink>Stato</InputLabel>
        <Select
          multiple
          displayEmpty
          label="Stato"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(0);
          }}
          renderValue={(selected) =>
            selected.length === 0
              ? "Tutti"
              : selected
                  .map(
                    (value) =>
                      NEWSLETTER_HISTORY_STATUS_FILTER_OPTIONS.find((option) => option.value === value)?.label || value
                  )
                  .join(", ")
          }
        >
          {NEWSLETTER_HISTORY_STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {hasFilters ? (
        <Tooltip title="Reset filtri">
          <IconButton size="small" onClick={resetFilters} aria-label="Reset filtri">
            <FilterAltOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>
        {count} risultati
      </Typography>
    </Stack>
  );

  const renderDetailToolbar = () => (
    <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setSelectedId(null)} sx={{ ml: "auto" }}>
      Torna alla lista
    </Button>
  );

  return (
    <>
      <ContentSectionPanel
        title={selectedId ? "Dettaglio pubblicazione" : "Storico pubblicazioni"}
        toolbar={selectedId ? renderDetailToolbar() : renderFiltersToolbar()}
        fillBody
        panelScroll
      >
        {showEmptyState ? (
          <Box sx={{ textAlign: "center", py: 6, flex: 1 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Non ci sono ancora pubblicazioni registrate.
            </Typography>
            {onNavigatePublish ? (
              <Button variant="contained" startIcon={<SendIcon />} onClick={onNavigatePublish}>
                Vai a invio newsletter
              </Button>
            ) : null}
          </Box>
        ) : selectedId ? (
          detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <NewsletterHistoryDetailPanel detail={detail} />
          )
        ) : (
          <NewsletterHistoryListPanel
            items={items}
            count={count}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onShowDetail={setSelectedId}
            onCancelScheduled={handleCancelScheduled}
            cancellingId={cancellingId}
            loading={loading}
          />
        )}
      </ContentSectionPanel>
      <ConfirmationModal
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancelScheduled}
        title="Annullare l'invio programmato?"
        subtitle=""
        message={
          cancelTarget
            ? `La newsletter "${cancelTarget.subject}" non verrà inviata e passerà allo stato annullato.`
            : ""
        }
        confirmText="CONFERMA"
      />
    </>
  );
};

NewsletterHistoryView.propTypes = {
  onNavigatePublish: PropTypes.func,
};

export default NewsletterHistoryView;
